// FAQ content for the state-level HUD / LIHTC data pages.
//
// Each set is rendered visibly on its page AND emitted as FAQPage JSON-LD, so
// the structured data always matches on-page content (Google requires this).
// Answers stay descriptive of published HUD data — no rate, approval, or
// pricing claims.

export type Faq = { q: string; a: string };

export function incomeLimitFaqs(stateName: string): Faq[] {
  return [
    {
      q: `What are HUD Income Limits in ${stateName}?`,
      a: `HUD publishes Income Limits every year for each county and metro area in ${stateName}. They set the household income thresholds used for Section 8 / Housing Choice Voucher eligibility, public housing, and LIHTC compliance, and they are expressed as a percentage of Area Median Income (AMI) for household sizes of one through eight people.`,
    },
    {
      q: `What do the 30%, 50%, and 80% AMI tiers mean?`,
      a: `30% of AMI is the Extremely Low Income limit, 50% is Very Low Income (the primary Housing Choice Voucher eligibility tier), and 80% is Low Income. LIHTC properties in ${stateName} generally restrict units to households at or below the 50% or 60% AMI thresholds.`,
    },
    {
      q: `How often do ${stateName} income limits change?`,
      a: `HUD refreshes Income Limits once per fiscal year, typically in April. This page reads the current published fiscal year directly from the HUD User API, so the county figures update as soon as HUD releases them.`,
    },
    {
      q: `Do income limits affect a DSCR loan on a Section 8 rental?`,
      a: `Income limits qualify the tenant, not the borrower. A DSCR loan is underwritten on the property's rent versus its payment, so the tenant's voucher eligibility matters mainly because it supports the contract rent used in the DSCR calculation. Terms are subject to lender guidelines and underwriter approval.`,
    },
  ];
}

export function fmrFaqs(stateName: string): Faq[] {
  return [
    {
      q: `What is a HUD Fair Market Rent in ${stateName}?`,
      a: `Fair Market Rent (FMR) is HUD's published estimate of gross rent — rent plus tenant-paid utilities — for standard-quality units by bedroom count in each ${stateName} county or metro area. Public housing agencies use FMR to set Housing Choice Voucher payment standards.`,
    },
    {
      q: `Is Fair Market Rent the same as the rent a Section 8 landlord receives?`,
      a: `Not necessarily. The housing agency sets a payment standard that is usually a percentage of FMR, then the approved contract rent depends on rent reasonableness, unit condition, and the tenant's income. The housing assistance payment (HAP) covers the agency's share and the tenant pays the balance.`,
    },
    {
      q: `How is FMR used when underwriting a rental in ${stateName}?`,
      a: `FMR is a useful market reference for modeling rental income when there is no executed lease, alongside the appraiser's rent schedule. Lenders underwrite to the lesser of documented rent or the appraiser's market rent, subject to program guidelines.`,
    },
    {
      q: `How current is the ${stateName} FMR data on this page?`,
      a: `The figures are pulled live from the HUD User API for the current published fiscal year. HUD revises FMRs annually, and mid-year Small Area FMR updates can apply in some metro areas.`,
    },
  ];
}

export function qctFaqs(stateName: string): Faq[] {
  return [
    {
      q: `What is a Qualified Census Tract in ${stateName}?`,
      a: `A Qualified Census Tract (QCT) is a HUD-designated census tract where at least half of households earn below 60% of Area Median Income, or where the poverty rate is 25% or higher. HUD publishes the ${stateName} designations each year.`,
    },
    {
      q: `Why does QCT status matter to investors?`,
      a: `A LIHTC development located in a QCT is eligible for a 30% basis boost, which increases the credits available for the project. QCTs also tend to concentrate voucher-holding households, which is why Section 8 investors watch them.`,
    },
    {
      q: `Is a QCT the same as a Difficult Development Area?`,
      a: `No. A QCT is based on income and poverty concentration, while a Difficult Development Area (DDA) is based on high construction, land, and utility costs relative to area incomes. Both can qualify a LIHTC project for the same 30% basis boost.`,
    },
    {
      q: `Can you finance a rental inside a ${stateName} QCT?`,
      a: `Yes — business-purpose loans on non-owner-occupied property are underwritten on the property and the borrower's experience, not on tract designation. Property condition, rent documentation, and market liquidity still drive the terms available, subject to lender approval.`,
    },
  ];
}

export function ddaFaqs(stateName: string): Faq[] {
  return [
    {
      q: `What is a Difficult Development Area in ${stateName}?`,
      a: `A Difficult Development Area (DDA) is a HUD-designated metro ZIP code or non-metro county where land, construction, and utility costs are high relative to Area Median Income. HUD republishes the ${stateName} list annually.`,
    },
    {
      q: `What is the LIHTC basis boost for a DDA?`,
      a: `A LIHTC project in a designated DDA may qualify for a 30% increase in eligible basis — the same boost available in a Qualified Census Tract — which raises the credit amount the project can claim.`,
    },
    {
      q: `What is the difference between an SDDA and a non-metro DDA?`,
      a: `Small Area DDAs (SDDAs) are designated at the ZIP-code level inside metro areas, so one ${stateName} metro can contain both designated and undesignated ZIPs. Non-metro DDAs are designated at the county level.`,
    },
    {
      q: `How do DDAs affect an investor buying a rental?`,
      a: `DDA status signals a high-cost submarket relative to local incomes, which usually means higher purchase prices and construction budgets. It does not change loan eligibility on its own; business-purpose financing is underwritten on the property's economics and lender guidelines.`,
    },
  ];
}
